/**
 * Tweet Ingestion & API Service
 * Handles tweet ID parsing, user profile extraction, and backend proxy communication
 * Supports modern x.com, twitter.com, and mobile links.
 */

import { API_ENDPOINTS } from '../configs/api.config';

/**
 * Extract tweet status ID from various URL formats
 */
export const extractTweetId = (url) => {
  if (!url || typeof url !== 'string') return null;

  const cleanUrl = url.trim();
  const pattern = /(?:twitter|x)\.com\/(?:#!\/)?[^/]+\/status(?:es)?\/(\d+)/i;
  const match = cleanUrl.match(pattern);

  if (match && match[1]) {
    return match[1];
  }

  // If the user pasted a raw status ID directly
  if (/^\d{10,22}$/.test(cleanUrl)) {
    return cleanUrl;
  }

  return null;
};

/**
 * Extract username from Twitter/X profile URL
 */
export const extractUsername = (url) => {
  if (!url || typeof url !== 'string') return null;

  const cleanUrl = url.trim();
  try {
    const parsed = new URL(cleanUrl.startsWith('http') ? cleanUrl : `https://${cleanUrl}`);
    const host = parsed.hostname.toLowerCase();

    if (!host.includes('twitter.com') && !host.includes('x.com')) {
      return null;
    }

    const segments = parsed.pathname.split('/').filter(Boolean);
    if (
      segments.length > 0 &&
      !['status', 'i', 'search', 'home', 'explore', 'notifications', 'messages'].includes(
        segments[0].toLowerCase()
      )
    ) {
      return segments[0];
    }
  } catch (e) {
    // ignore parse errors
  }

  return null;
};

/**
 * Fetch tweet data by ID via backend proxy (falls back to Twitter syndication if needed)
 */
export const fetchTweetData = async (tweetId) => {
  if (!tweetId) throw new Error('Tweet ID is required');

  try {
    // 1. Try local backend proxy
    const res = await fetch(API_ENDPOINTS.TWEET(tweetId));
    if (res.ok) {
      const responseJson = await res.json();
      const tweet = responseJson?.data || responseJson;
      if (tweet && tweet.id) {
        return normalizeTweetData(tweet);
      }
    }
  } catch (err) {
    console.warn('Backend proxy fetch failed, attempting public syndication fallback:', err);
  }

  // 2. Fallback to Twitter public syndication widget CDN
  try {
    const syndicationUrl = `https://cdn.syndication.twimg.com/tweet-result?id=${tweetId}&token=4`;
    const synRes = await fetch(syndicationUrl);
    if (synRes.ok) {
      const synData = await synRes.json();
      if (synData && synData.id_str) {
        return normalizeSyndicationData(synData);
      }
    }
  } catch (synErr) {
    console.warn('Syndication fallback failed:', synErr);
  }

  throw new Error('Unable to load tweet. The post may be private, deleted, or rate-limited.');
};

/**
 * Fetch thread conversation tweets
 */
export const fetchThreadData = async (tweetId, limit = 5) => {
  if (!tweetId) return null;

  try {
    const url = `${API_ENDPOINTS.THREAD(tweetId)}?limit=${limit}`;
    const res = await fetch(url);
    if (res.ok) {
      const json = await res.json();
      return json?.data || json;
    }
  } catch (err) {
    console.warn('Failed to fetch thread replies:', err);
  }
  return null;
};

/**
 * Fetch Twitter profile metadata
 */
export const fetchProfileData = async (username) => {
  if (!username) throw new Error('Username is required');

  try {
    const res = await fetch(API_ENDPOINTS.USER_PROFILE(username));
    if (res.ok) {
      const data = await res.json();
      return {
        id: data.id || `profile-${username}`,
        url: `https://x.com/${username}`,
        isProfile: true,
        author: {
          name: data.name || username,
          username: data.username || username,
          verified: !!data.verified,
          avatar: data.profile_image_url || '',
          profile_image_url: data.profile_image_url || '',
          description: data.description || '',
          location: data.location || '',
          followers_count: data.followers_count || 0,
          following_count: data.following_count || 0,
          posts_count: data.tweets_count || 0,
        },
        text: data.description || '',
        createdAt: new Date().toISOString(),
        metrics: { likes: 0, retweets: 0, replies: 0 },
        media: [],
      };
    }
  } catch (err) {
    console.warn('Profile fetch failed:', err);
  }

  throw new Error(`Unable to load profile @${username}. Please check the handle and try again.`);
};

/**
 * Normalize backend Twitter API v2 payload into a standard UI format
 */
function normalizeTweetData(tweet) {
  const author = tweet.author || {};
  const metrics = tweet.public_metrics || {};

  return {
    id: tweet.id,
    text: tweet.text || '',
    author: {
      id: author.id,
      name: author.name || 'User',
      username: author.username || 'user',
      verified: !!author.verified,
      avatar: author.profile_image_url || '',
      profile_image_url: author.profile_image_url || '',
    },
    created_at: tweet.created_at || new Date().toISOString(),
    conversation_id: tweet.conversation_id || tweet.id,
    public_metrics: {
      like_count: metrics.like_count || 0,
      retweet_count: metrics.retweet_count || 0,
      reply_count: metrics.reply_count || 0,
      quote_count: metrics.quote_count || 0,
      bookmark_count: metrics.bookmark_count || 0,
      impression_count: metrics.impression_count || 0,
    },
    media: tweet.media || [],
    quoted_tweet: tweet.quoted_tweet || null,
  };
}

/**
 * Normalize public syndication response
 */
function normalizeSyndicationData(data) {
  const user = data.user || {};
  const photos = (data.photos || []).map((p) => ({
    type: 'photo',
    url: p.url,
    width: p.width,
    height: p.height,
  }));

  return {
    id: data.id_str,
    text: data.text || '',
    author: {
      name: user.name || 'User',
      username: user.screen_name || 'user',
      verified: !!user.verified || !!user.is_blue_verified,
      avatar: user.profile_image_url_https || '',
      profile_image_url: user.profile_image_url_https || '',
    },
    created_at: data.created_at || new Date().toISOString(),
    conversation_id: data.conversation_count_id || data.id_str,
    public_metrics: {
      like_count: data.favorite_count || 0,
      retweet_count: data.retweet_count || 0,
      reply_count: data.reply_count || data.conversation_count || 0,
      quote_count: data.quote_count || 0,
      bookmark_count: data.bookmark_count || 0,
      impression_count: data.views?.count || data.impression_count || 0,
    },
    media: photos,
    quoted_tweet: data.quoted_tweet ? normalizeSyndicationData(data.quoted_tweet) : null,
  };
}

const tweetService = {
  extractTweetId,
  extractUsername,
  fetchTweetData,
  fetchThreadData,
  fetchProfileData,
};

export default tweetService;
